<template>
    <div>
        <div class="px-4 mb-4">
            <DField id="Search">
                <DInput
                    v-model="search"
                    aria-label="Search"
                    left-icon="magnifying-glass"
                    placeholder="Search"
                    rounded
                />
            </DField>
        </div>
        <div class="flex mx-4 items-center justify-between">
            <div class="font-medium text-xl">
                {{ config.data.list_title }}
                <DBadge
                    v-if="config.data.badge"
                    class="mb-2 mr-1"
                    :type="config.data.badge.color"
                    :icon="config.data.badge.icon"
                    rounded
                >
                    {{ config.data.badge.text }}
                </DBadge>
            </div>
            <DButton
                v-if="hasSelectableItems"
                theme="text-link"
                @click="toggleSelectAll"
            >
                {{ someSelected ? 'Deselect all' : 'Select all' }}
            </DButton>
        </div>
        <template
            v-for="(item, index) in items"
            :key="`item-${item.id}`"
        >
            <div
                v-if="isShown(item)"
                class="my-6 mx-4 flex items-center"
                data-cy="selection-list-item"
            >
                <div
                    v-if="item.disabled && item.disabled_icon"
                    class="w-10"
                >
                    <UIIcon
                        color="#9FA6B2"
                        :icon="item.disabled_icon"
                        weight="fill"
                        :size="22"
                    />
                </div>
                <DCheckbox
                    v-else-if="!item.disabled"
                    v-model="selected[index]"
                    class="scale-[1.3]"
                />
                <div
                    class="flex items-center w-full"
                    @click="handleItemClicked(index, item)"
                >
                    <div :class="{ 'ml-4': !item.disabled && !item.disabled_icon }">
                        <div class="text-neutral-base">
                            {{ item.title }}
                        </div>
                        <div class="text-neutral-empty text-sm">
                            {{ item.subtitle }}
                        </div>
                    </div>
                    <div class="ml-auto flex gap-2">
                        <UIIcon
                            v-for="icon in item.icons"
                            :key="`item-${item.id}-icon-${icon}`"
                            :icon="icon"
                            weight="bold"
                            :size="16"
                        />
                    </div>
                </div>
            </div>
        </template>
        <Teleport :to="footerMenu?.$el">
            <div class="pb-(--beyond-safe-area-inset-bottom)">
                <div
                    v-if="someSelected"
                    class="p-4 border-t border-neutral-base flex surface-primary w-full mt-auto gap-2"
                >
                    <DButton
                        left-icon="ph:note"
                        theme="secondary"
                        wide
                        @click="showBulkNoteModal"
                    >
                        Add note
                    </DButton>
                    <DButton
                        wide
                        @click="showBulkActionModal"
                    >
                        Mark as...
                    </DButton>
                </div>
                <div
                    v-else-if="config.data.complete_url"
                    class="p-4 border-t border-neutral-base flex surface-primary w-full mt-auto gap-2"
                >
                    <DButton
                        wide
                        :disabled="markingComplete"
                        @click="saveCompletion"
                    >
                        Complete attendance
                    </DButton>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
    import mitt from 'mitt'
    import { useRouter } from 'vue-router'
    import {
        DField, DInput, DButton, DCheckbox, DBadge,
    } from '@digistorm/spark'
    import { modalController } from '@ionic/vue'
    import { transform } from 'lodash'
    import PhIcon from '@/components/app/PhIcon.vue'
    import UIIcon from '@/components/ui/UIIcon.vue'
    import SelectionListActionModal
        from '@/components/ui/SelectionList/SelectionListActionModal.vue'
    import { useLoadingOverlays } from '@/composables/loadingOverlays'

    const router = useRouter()

    const moduleHelpers = useModuleHelpers()
    const { snackbar } = useSnackbars()
    const loadingOverlays = useLoadingOverlays()

    const props = defineProps({
        config: Object,
    })

    const footerMenu = inject('footerMenu')

    const search = ref('')
    const selected = ref([])
    const items = ref(props.config.items)
    const itemForReplacement = ref(null)
    const markingComplete = ref(false)
    const savingBulkNote = ref(false)
    const contextualEvents = ref(mitt())

    watch(() => props.config.items, (newVal) => {
        items.value = newVal
    })

    const hasSelectableItems = computed(() => {
        return items.value.length && items.value.some((item) => !item.disabled)
    })

    const someSelected = computed(() => {
        return selected.value.length && selected.value.some((value) => value)
    })

    const selectedIds = computed(() => {
        return transform(selected.value, (result, isSelected, index) => {
            if (isSelected) {
                result.push(items.value[index].id)
            }
        }, [])
    })

    const isShown = (item) => {
        return !search.value || item.title.toLowerCase().includes(search.value.toLowerCase())
    }

    const deselectAll = () => {
        selected.value = items.value.map(() => false)
    }

    const toggleSelectAll = () => {
        selected.value = items.value.map((item) => !someSelected.value && !item.disabled)
    }

    const saveCompletion = () => {
        markingComplete.value = true
        loadingOverlays.push()

        moduleHelpers.handleClick(router, {
            link: {
                type: 'api-action',
                url: props.config.data.complete_url,
            },
        }, null, true, contextualEvents.value)
            .catch((e) => {
                snackbar({
                    message: 'Failed to save completion',
                    type: 'error',
                })
            })
            .finally(() => {
                loadingOverlays.pop()
                markingComplete.value = false
            })
    }

    const saveBulkAction = (action, params) => {
        loadingOverlays.push()
        moduleHelpers.handleClick(router, {
            link: {
                type: 'api-action',
                url: props.config.data.bulk_action_url,
                payload: {
                    ids: selectedIds.value,
                    action: action.value, // "value" of SelectionActionData
                    ...params,
                },
            },
        }, null, true, contextualEvents.value)
            .catch((e) => {
                snackbar({
                    message: 'Failed to save bulk action',
                    type: 'error',
                })
            })
            .finally(() => {
                deselectAll()
                loadingOverlays.pop()
            })
    }

    const saveBulkNote = (note) => {
        if (savingBulkNote.value) {
            return
        }
        savingBulkNote.value = true
        loadingOverlays.push()
        moduleHelpers.handleClick(router, {
            link: {
                type: 'api-action',
                url: props.config.data.bulk_action_url,
                payload: {
                    ids: selectedIds.value,
                    note,
                },
            },
        }, null, true, contextualEvents.value)
            .catch((e) => {
                snackbar({
                    message: 'Failed to save bulk note',
                    type: 'error',
                })
            })
            .finally(() => {
                savingBulkNote.value = false
                deselectAll()
                loadingOverlays.pop()
            })
    }

    const saveItem = (index, item, action, params) => {
        loadingOverlays.push()

        // Save index for replacement
        itemForReplacement.value = index

        moduleHelpers.handleClick(router, {
            link: {
                type: 'api-action',
                url: item.action_url,
                payload: {
                    action: action?.value ?? item.status_value, // "value" of SelectionActionData
                    ...params,
                },
            },
        }, null, true, contextualEvents.value)
            .catch((e) => {
                snackbar({
                    message: 'Failed to save item',
                    type: 'error',
                })
            })
            .finally(() => {
                // Item should be replaced
                itemForReplacement.value = null
                loadingOverlays.pop()
            })
    }

    const createModal = (props, initialBreakpoint = 1, breakpoints = [0, 0.7, 1]) => {
        return modalController.create({
            component: SelectionListActionModal,
            initialBreakpoint,
            breakpoints,
            componentProps: props,
            handle: true,
            cssClass: 'ds-modal-bottom-sheet',
        })
    }

    const showActionModal = async (index, item) => {
        // Flag if these were modified in the modal
        let action = null
        let params = {}
        let modified = false

        let breakpoint // undefined
        if (item.disabled) {
            // For disabled items we want a shorter modal
            // However, if there are multiple notes, we need more space for them
            let elementCount = item.notes?.reduce((count, note) => count + (note.note ? 1 : 0), 0)
            if (item.time_field) {
                elementCount += 1
            }
            if (elementCount === 0) {
                breakpoint = 0.3
            } else if (elementCount === 1) {
                breakpoint = 0.5
            } else {
                breakpoint = 0.7
            }
        }

        const modal = await createModal(
            {
                title: props.config.data.actions_title,
                actions: props.config.data.actions,
                item,
                onAction: (newAction, entry) => {
                    action = newAction
                    params = {
                        ...params,
                        ...entry,
                    }
                    modified = true
                    modal.dismiss()
                },
                onForm: (entry) => {
                    params = {
                        ...params,
                        ...entry,
                    }
                    modified = true
                },
                onNotes: (newNotes) => {
                    newNotes.forEach((note) => {
                        params[note.key] = note.note
                    })
                    modified = true
                },
                onContentSize: (isSmall) => {
                    modal.setCurrentBreakpoint(isSmall ? 0.7 : 1)
                },
            },
            breakpoint,
            // Undefined needs to be passed, not null
            breakpoint && breakpoint < 0.7 ? [breakpoint, 0.7, 1] : undefined,
        )
        modal.onDidDismiss().then(() => {
            if (modified) {
                saveItem(index, item, action, params)
            }
        })
        modal.present()
    }

    const showBulkActionModal = async () => {
        const modal = await createModal({
            title: props.config.data.actions_title,
            actions: props.config.data.actions,
            onAction: (action, params) => {
                modal.dismiss()
                saveBulkAction(action, params)
            },
        })
        modal.present()
    }

    const showBulkNoteModal = async () => {
        const modal = await createModal({
            title: 'Add note',
            noteOnly: true,
            onNote: (note) => {
                modal.dismiss()
                saveBulkNote(note)
            },
        }, 0.5)
        modal.present()
    }

    const handleItemClicked = (index, item) => {
        showActionModal(index, item)
    }

    const handleReplaceItem = (event) => {
        const item = event.payload
        if (itemForReplacement.value !== null) {
            items.value[itemForReplacement.value] = item
        }
    }

    onMounted(() => {
        contextualEvents.value.on('replace-item', handleReplaceItem)
    })

    onUnmounted(() => {
        contextualEvents.value.off('replace-item', handleReplaceItem)
    })

</script>
